/**
 * LYTICK Session Store
 *
 * Persists and restores the last meaningful workspace state after reload.
 * This is separate from settingsStore (user preferences) — this is about
 * "where the user left off" so the workspace feels continuous.
 *
 * Stored data:
 *   - symbol, timeframe, chartType
 *   - active indicators (IDs only)
 *   - drawing overlay snapshots (toolId, color, points, extendData, visible, locked)
 *   - drawing color preference
 *
 * Intentionally NOT persisted:
 *   - UI state (modals, drawers, orientation)
 *   - Live price data / candle data (re-fetched fresh)
 *   - Replay session (restarting replay after refresh is unsafe — candle data
 *     may be stale; user must re-initiate replay)
 *   - Active tool / selected drawing (transient interaction state)
 *   - Toast notifications
 *   - Connection state
 */

import { create } from "zustand";
import type { DrawingToolId } from "./drawingStore";
import type { IndicatorInstance } from "@/types/indicator";
import { safeSetItem } from "@/lib/lytick/safe-storage";

// ─── Types ──────────────────────────────────────────────────────────────

/** Serialized overlay point — only the data KLineCharts needs to reconstruct */
export interface SerializedOverlayPoint {
  timestamp: number;
  value: number;
  dataIndex?: number;
}

/** A single drawing snapshot — enough to reconstruct via createOverlay() */
export interface SerializedDrawing {
  toolId: DrawingToolId;
  color: string;
  points: SerializedOverlayPoint[];
  extendData?: unknown;
  visible: boolean;
  locked: boolean;
}

/** The full session snapshot */
export interface SessionSnapshot {
  symbol: string;
  symbolName: string;
  timeframe: string;
  chartType: string;
  indicators: IndicatorInstance[];
  drawings: SerializedDrawing[];
  drawingColor: string;
  savedAt: number;
}

// ─── Storage key ────────────────────────────────────────────────────────

const STORAGE_KEY = "lytick-session";

// ─── Helpers ────────────────────────────────────────────────────────────

function loadFromStorage(): SessionSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionSnapshot;
    // Sanity check: must have recent timestamp (within 30 days) and core fields
    if (
      !parsed ||
      typeof parsed.savedAt !== "number" ||
      Date.now() - parsed.savedAt > 30 * 24 * 60 * 60 * 1000 ||
      !parsed.symbol ||
      !parsed.timeframe
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function saveToStorage(snapshot: SessionSnapshot): void {
  if (typeof window === "undefined") return;
  safeSetItem(STORAGE_KEY, JSON.stringify(snapshot), "session");
}

// ─── Debounced save ─────────────────────────────────────────────────────

let saveTimer: ReturnType<typeof setTimeout> | null = null;
const SAVE_DELAY_MS = 800;

function debouncedSave(snapshot: SessionSnapshot): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveToStorage(snapshot);
    saveTimer = null;
  }, SAVE_DELAY_MS);
}

// ─── Store ──────────────────────────────────────────────────────────────

interface SessionState {
  /** Whether a previous session was found on boot */
  hasPreviousSession: boolean;
  /** The last saved snapshot (null before first save) */
  lastSnapshot: SessionSnapshot | null;
  /** Drawings queued for restore after a CSV file is loaded */
  pendingRestoreDrawings: SerializedDrawing[] | null;

  // Actions
  /** Check for a previous session (call once on boot, returns snapshot or null) */
  checkPreviousSession: () => SessionSnapshot | null;
  /** Save a full session snapshot (debounced) */
  saveSession: (snapshot: SessionSnapshot) => void;
  /** Save immediately (non-debounced, for beforeunload) */
  saveSessionNow: (snapshot: SessionSnapshot) => void;
  /** Clear saved session */
  clearSession: () => void;
  /** Queue drawings to be restored after the next chart data load */
  setPendingRestoreDrawings: (drawings: SerializedDrawing[] | null) => void;
}

export const useSessionStore = create<SessionState>()((set, get) => ({
  hasPreviousSession: false,
  lastSnapshot: null,
  pendingRestoreDrawings: null,

  checkPreviousSession: () => {
    const snapshot = loadFromStorage();
    set({
      hasPreviousSession: snapshot !== null,
      lastSnapshot: snapshot,
    });
    return snapshot;
  },

  saveSession: (snapshot) => {
    set({ lastSnapshot: snapshot });
    debouncedSave(snapshot);
  },

  saveSessionNow: (snapshot) => {
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    set({ lastSnapshot: snapshot });
    saveToStorage(snapshot);
  },

  clearSession: () => {
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEY);
    }
    set({ hasPreviousSession: false, lastSnapshot: null });
  },

  setPendingRestoreDrawings: (drawings) => {
    set({ pendingRestoreDrawings: drawings });
  },
}));
